import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, MonitorPlay, PauseCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRemaining, useRoomSocket } from "@/lib/ws";
import { TOKEN_KEY } from "@/lib/api";
import { getMode } from "@/modes";
import { ControlBar } from "@/components/game/ControlBar";
import { StudentsTable } from "@/components/game/StudentsTable";
import { BadgeButton } from "@/components/game/BadgeCard";
import { RoomSettingsForm } from "@/components/game/SettingFields";
import { TeamNames } from "@/components/game/common";
import { PlaylistBar } from "@/components/teacher/PlaylistBar";

const PHASE = { lobby: "Lobi", wheel: "Çark", question: "Soru açık", results: "Sonuç ekranı", activity: "Etkinlik sürüyor", finished: "Oyun bitti" };

const Header = ({ state, remaining }) => {
  const url = `${window.location.origin}/katil/${state.code}`;
  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="flex-1 min-w-0">
        <p className="eyebrow">{state.mode_label}</p>
        <h1 className="font-display text-5xl font-black text-[#FACC15] tracking-widest" data-testid="live-game-code">{state.code}</h1>
        <p className="text-slate-300 mt-1" data-testid="live-phase">
          {PHASE[state.phase]}{state.paused && " · Duraklatıldı"}
          {state.phase === "question" && ` · ${Math.ceil(remaining / 1000)} sn`}
          {state.q_index > 0 && ` · Soru ${state.q_index}/${state.question_count}`}
        </p>
      </div>
      {state.badge && <BadgeButton badge={state.badge} />}
      <Button variant="outline" onClick={() => { navigator.clipboard.writeText(url); toast.success("Katılım bağlantısı kopyalandı"); }} data-testid="copy-join-link"><Copy className="h-4 w-4 mr-2" />Katılım bağlantısı</Button>
      <Button onClick={() => window.open(`/tahta/${state.code}`, "_blank")} className="font-bold" data-testid="open-board-button"><MonitorPlay className="h-4 w-4 mr-2" />Tahtayı aç</Button>
    </div>
  );
};

export default function LiveGamePage() {
  const { code } = useParams();
  const token = localStorage.getItem(TOKEN_KEY);
  const { state, fatal, send, deadline } = useRoomSocket(`/teacher/${code}?token=${encodeURIComponent(token || "")}`);
  const remaining = useRemaining(state, deadline);

  if (fatal) return <p className="text-xl" data-testid="live-error">Oyun açılamadı.</p>;
  if (!state) return <p className="eyebrow" data-testid="live-connecting">Bağlanılıyor…</p>;
  const Mode = getMode(state.mode);
  const lobby = state.phase === "lobby";
  const update = (patch) => send({ type: "cmd", action: "update_settings", settings: patch });

  return (
    <div className="space-y-6" data-testid="live-game-page">
      <Link to="/ogretmen/oyunlar" className="inline-flex items-center gap-2 text-slate-400 hover:text-white" data-testid="back-to-games"><ArrowLeft className="h-4 w-4" />Oyunlar</Link>
      <Header state={state} remaining={remaining} />
      <PlaylistBar code={code} finished={state.phase === "finished"} />
      <div className="panel p-4 sticky top-2 z-20"><ControlBar state={state} send={send} /></div>
      {state.paused && <div className="flex items-center gap-3 rounded-lg border border-amber-600/60 bg-amber-950/30 p-3 text-amber-200" data-testid="live-paused"><PauseCircle className="h-5 w-5" />Oyun duraklatıldı. Devam etmek için "Devam et"e basın.</div>}
      <div className="grid xl:grid-cols-[1fr_380px] gap-6 items-start">
        <div className="space-y-6 min-w-0">
          {lobby && (
            <div className="panel p-5 space-y-4">
              <h3 className="font-display text-xl font-bold">Takım adları</h3>
              <TeamNames key={state.teams.map((t) => t.id).join()} state={state} send={send} />
            </div>
          )}
          {!lobby && <Mode.TeacherView state={state} send={send} />}
          <details className="panel p-5" open={lobby}>
            <summary className="font-display text-xl font-bold cursor-pointer" data-testid="live-settings-toggle">Ayarlar</summary>
            <div className="pt-4"><RoomSettingsForm settings={state.settings} onChange={update} lobby={lobby} ModeForm={Mode.SettingsForm} /></div>
          </details>
        </div>
        <StudentsTable state={state} send={send} />
      </div>
    </div>
  );
}
